import { Link } from "@tanstack/react-router";
import { internalPath } from "@/lib/paths";

export interface ContextLink {
  /** Internal path, with or without trailing slash. */
  href: string;
  /** Descriptive anchor text. Never "läs mer". */
  label: string;
  /** Optional short line under the anchor. */
  blurb?: string;
}

/**
 * Small contextual internal-link block. Renders real anchors through
 * TanStack `Link`, always to the canonical trailing-slash path.
 */
export function ContextLinkBlock({
  heading,
  intro,
  links,
  footer,
}: {
  heading: string;
  intro?: string;
  links: ContextLink[];
  footer?: React.ReactNode;
}) {
  if (links.length === 0 && !footer) return null;

  return (
    <section className="container-page py-10">
      <h2 className="text-2xl md:text-3xl">{heading}</h2>
      {intro && <p className="mt-3 text-foreground/80 max-w-3xl">{intro}</p>}
      {links.length > 0 && (
        <ul className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl">
          {links.map((l) => (
            <li key={l.href} className="card-elev p-5 hover:border-brass transition-colors">
              <Link
                to={internalPath(l.href)}
                className="font-semibold text-brass-deep hover:underline"
              >
                {l.label}
              </Link>
              {l.blurb && (
                <span className="block text-sm text-muted-foreground mt-1">{l.blurb}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {footer && <div className="mt-5 text-foreground/80 max-w-3xl">{footer}</div>}
    </section>
  );
}
